import { useEffect, useRef, useState } from 'react'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

/* ─── Stats ─────────────────────────────────────────────────── */
const STAT_CONFIGS = [
  { key: 's1l', value: 96000, suffix: '+', prefix: '' },
  { key: 's2l', value: 411,   suffix: 'Bn', prefix: 'AED ' },
  { key: 's3l', value: 70,    suffix: '%',  prefix: '' },
  { key: 's4l', value: 200,   suffix: '+',  prefix: '' },
]

/* ─── Timeline ───────────────────────────────────────────────── */
const TIMELINE_KEYS = [
  { year: '2004', icon: '🏗️', titleKey: 't1t', descKey: 't1d' },
  { year: '2010', icon: '🌆', titleKey: 't2t', descKey: 't2d' },
  { year: '2019', icon: '🪙', titleKey: 't3t', descKey: 't3d' },
  { year: '2022', icon: '📈', titleKey: 't4t', descKey: 't4d' },
  { year: '2024', icon: '🚀', titleKey: 't5t', descKey: 't5d' },
  { year: '2030', icon: '🌐', titleKey: 't6t', descKey: 't6d' },
]

/* ─── Animated counter hook ─────────────────────────────────── */
function useCounter(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!start) return
    let startTime = null
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.floor(eased * target))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [target, duration, start])
  return count
}

function StatCard({ stat, trigger }) {
  const count = useCounter(stat.value, 2000, trigger)
  return (
    <div
      className="evo-stat-card"
      style={{
        background: 'rgba(255,255,255,0.06)',
        border: `1px solid ${GOLD}25`,
        borderRadius: '16px',
        padding: '32px 24px',
        textAlign: 'center',
        backdropFilter: 'blur(8px)',
        transition: 'border-color 0.3s, background 0.3s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '160px',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${GOLD}60`; e.currentTarget.style.background = 'rgba(255,255,255,0.09)' }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${GOLD}25`; e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
    >
      <div
        className="evo-stat-number"
        style={{
          fontFamily: "'Georgia', serif",
          fontSize: 'clamp(34px, 4vw, 52px)',
          fontWeight: 700,
          color: GOLD,
          lineHeight: 1,
          letterSpacing: '-1px',
        }}
      >
        {stat.prefix}{count.toLocaleString()}{stat.suffix}
      </div>
      <div style={{
        fontFamily: FONT,
        fontSize: '13px',
        color: 'rgba(255,255,255,0.55)',
        marginTop: '10px',
        lineHeight: 1.5,
        maxWidth: '160px',
        margin: '10px auto 0',
      }}>
        {stat.label}
      </div>
    </div>
  )
}

function DubaiEvolutionSection() {
  const [triggered, setTriggered] = useState(false)
  const sectionRef = useRef(null)
  const { t } = useLanguage()

  const STATS = STAT_CONFIGS.map(s => ({ ...s, label: t(`evo.stats.${s.key}`) }))
  const TIMELINE = TIMELINE_KEYS.map(item => ({
    year: item.year,
    icon: item.icon,
    title: t(`evo.timeline.${item.titleKey}`),
    desc: t(`evo.timeline.${item.descKey}`),
  }))

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTriggered(true); observer.disconnect() } },
      { threshold: 0.2 }
    )
    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div style={{ width: '100%' }}>
      <style>{`
        .evo-timeline-card {
          background: #fff;
          border-radius: 14px;
          padding: 24px;
          border: 1px solid rgba(0,46,83,0.08);
          position: relative;
          transition: all 0.3s ease;
        }
        .evo-timeline-card:hover {
          border-color: ${GOLD}50;
          box-shadow: 0 8px 28px rgba(197,160,89,0.12);
          transform: translateY(-4px);
        }
        @media (max-width: 900px) {
          .evo-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .evo-timeline-grid { grid-template-columns: 1fr 1fr !important; }
          .evo-stats-section { padding: 60px 16px !important; }
          .evo-timeline-section { padding: 52px 16px !important; }
        }
        @media (max-width: 600px) {
          .evo-stats-grid { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
          .evo-timeline-grid { grid-template-columns: 1fr !important; }
          .evo-stat-card { padding: 20px 14px !important; min-height: 130px !important; }
          .evo-stat-number { font-size: clamp(26px, 7vw, 38px) !important; }
          .evo-stats-section { padding: 48px 16px !important; }
          .evo-timeline-section { padding: 48px 16px !important; }
        }
      `}</style>

      {/* ─── Stats Section ─────────────────────── */}
      <div
        ref={sectionRef}
        className="evo-stats-section"
        style={{
          background: `linear-gradient(160deg, ${NAVY} 0%, #001222 100%)`,
          padding: '80px 20px',
          width: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: '-120px', right: '-80px', width: '500px', height: '500px', borderRadius: '50%', background: `${GOLD}07`, pointerEvents: 'none' }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '52px' }}>
            <span style={{
              display: 'inline-block', background: `${GOLD}22`, color: GOLD,
              fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
              padding: '5px 16px', borderRadius: '50px', textTransform: 'uppercase',
              border: `1px solid ${GOLD}40`, marginBottom: '14px',
            }}>
              { t('evo.statsBadge') }
            </span>
            <h2 style={{
              fontFamily: FONT, fontSize: 'clamp(26px, 3.5vw, 42px)', fontWeight: 700,
              color: '#fff', margin: 0, lineHeight: 1.2, letterSpacing: '-0.5px',
            }}>
              { t('evo.statsHeading') }
            </h2>
            <p style={{
              fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.5)',
              marginTop: '12px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6,
            }}>
              { t('evo.statsDesc') }
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }} className="evo-stats-grid">
            {STATS.map((stat, i) => (
              <StatCard key={i} stat={stat} trigger={triggered} />
            ))}
          </div>
        </div>
      </div>

      {/* ─── Timeline Section ──────────────────── */}
      <div className="evo-timeline-section" style={{ background: '#f4f7fb', padding: '72px 20px', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{
              display: 'inline-block', background: `${GOLD}18`, color: GOLD,
              fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
              padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
              border: `1px solid ${GOLD}35`, marginBottom: '12px',
            }}>
              { t('evo.timelineBadge') }
            </span>
            <h2 style={{
              fontFamily: FONT, fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700,
              color: NAVY, margin: 0, letterSpacing: '-0.3px',
            }}>
              { t('evo.timelineHeading') }
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '22px' }} className="evo-timeline-grid">
            {TIMELINE.map((item, i) => (
              <div key={i} className="evo-timeline-card">
                {/* Year badge */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: `${GOLD}15`, color: GOLD,
                  fontFamily: FONT, fontSize: '12px', fontWeight: 700,
                  padding: '4px 12px', borderRadius: '50px',
                  border: `1px solid ${GOLD}30`, marginBottom: '14px',
                }}>
                  {item.year}
                </div>
                <div style={{ fontSize: '26px', marginBottom: '10px' }}>{item.icon}</div>
                <div style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, marginBottom: '8px' }}>
                  {item.title}
                </div>
                <div style={{ fontFamily: FONT, fontSize: '13px', color: '#5a6577', lineHeight: 1.65 }}>
                  {item.desc}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <p style={{ fontFamily: FONT, fontSize: '15px', color: '#6b7a8d', margin: '0 0 18px' }}>
              { t('evo.timelineCTA') }
            </p>
            <a href="/contact" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: NAVY, color: '#fff',
              fontFamily: FONT, fontSize: '14px', fontWeight: 600,
              padding: '13px 28px', borderRadius: '10px', textDecoration: 'none',
              transition: 'background 0.2s, transform 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#001a33'; e.currentTarget.style.transform = 'translateY(-2px)' }}
              onMouseLeave={e => { e.currentTarget.style.background = NAVY; e.currentTarget.style.transform = 'translateY(0)' }}
            >
              { t('evo.timelineBtn') }
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DubaiEvolutionSection
